import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import {
  services,
  contactInfo,
  companyDetails,
} from "@/i18n/translations";

const Footer = () => {
  const { lang, t } = useLanguage();
  const year = new Date().getFullYear();

  return (
    <footer className="relative bg-cream border-t border-navy/10 mt-24" data-testid="site-footer">
      <div className="px-6 sm:px-10 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="inline-block text-center" data-testid="footer-brand">
              <Link to="/" className="inline-block">
                <img
                  src="/assets/brand/beyond-the-ordinary.png"
                  alt="Rota Yacht Agency · Beyond the Ordinary"
                  className="h-24 w-auto mx-auto"
                />
              </Link>
              <div className="mt-6 max-w-sm">
                <p
                  className="font-display italic text-lg leading-tight"
                  style={{ color: "#C9A24A" }}
                >
                  {t.footer.taglineLead}
                </p>
                <div className="mt-2 flex items-center justify-center gap-2.5">
                  <span className="w-5 h-px bg-navy/20" />
                  <span className="text-[9px] tracking-[0.3em] uppercase font-medium text-navy/45">
                    {t.footer.taglineCoda}
                  </span>
                  <span className="w-5 h-px bg-navy/20" />
                </div>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-5">{t.footer.services}</div>
            <ul className="space-y-2">
              {services.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="text-sm text-navy/70 hover:text-brand transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s[lang].name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/services" className="text-sm text-brand hover:text-brand-soft">
                  {t.common.allServices} →
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline mb-5">{t.footer.coverage}</div>
            <ul className="space-y-2">
              {[
                { key: "sinop", to: "/destinations/sinop", labels: { en: "Sinop", ru: "Синоп", tr: "Sinop" } },
                { key: "samsun", to: "/destinations/samsun", labels: { en: "Samsun", ru: "Самсун", tr: "Samsun" } },
                { key: "trabzon", to: "/destinations/trabzon", labels: { en: "Trabzon", ru: "Трабзон", tr: "Trabzon" } },
                { key: "amasra", to: "/destinations/amasra", labels: { en: "Amasra", ru: "Амасра", tr: "Amasra" } },
                { key: "istanbul", to: "/destinations/istanbul", labels: { en: "Istanbul", ru: "Стамбул", tr: "İstanbul" } },
                { key: "canakkale", to: "/destinations/canakkale", labels: { en: "Çanakkale", ru: "Чанаккале", tr: "Çanakkale" } },
              ].map((d) => (
                <li key={d.key}>
                  <Link
                    to={d.to}
                    className="text-sm text-navy/70 hover:text-brand transition-colors"
                    data-testid={`footer-destination-${d.key}`}
                  >
                    {d.labels[lang] || d.labels.en}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/destinations"
                  className="text-sm text-brand hover:text-brand-soft"
                  data-testid="footer-destination-other"
                >
                  {lang === "ru" ? "Другие" : lang === "tr" ? "Diğer" : "Other"} →
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline mb-5">{t.nav.contact}</div>
            <ul className="space-y-3 text-sm text-navy/80">
              <li className="flex items-start gap-3">
                <Mail size={15} className="text-brand mt-0.5 shrink-0" strokeWidth={1.5} />
                <a href={`mailto:${contactInfo.email}`} className="hover:text-brand transition-colors">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={15} className="text-brand mt-0.5 shrink-0" strokeWidth={1.5} />
                <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} className="hover:text-brand transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={15} className="text-brand mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>Sinop · İstanbul · Amasra</span>
              </li>
              <li className="flex items-start gap-3">
                <Instagram size={15} className="text-brand mt-0.5 shrink-0" strokeWidth={1.5} />
                <a
                  href={contactInfo.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-brand transition-colors"
                  data-testid="footer-instagram-link"
                >
                  @{contactInfo.instagram}
                </a>
              </li>
            </ul>

            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={`https://wa.me/${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block border border-brand text-brand px-4 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-brand hover:text-white transition-colors"
                data-testid="footer-whatsapp-cta"
              >
                WhatsApp
              </a>
              <a
                href={`https://t.me/+${contactInfo.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block border border-[#229ED9] text-[#229ED9] px-4 py-2 text-[11px] tracking-[0.2em] uppercase hover:bg-[#229ED9] hover:text-white transition-colors"
                data-testid="footer-telegram-cta"
              >
                Telegram
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-navy/10 text-xs text-navy/40">
          <p className="text-center md:text-left mb-3" data-testid="footer-disclaimer">
            {t.footer.disclaimer}
          </p>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div>
              © {year} {companyDetails.legalName}. {t.footer.rights}
            </div>
            <div className="flex gap-5">
              <span>License No. {companyDetails.agencyLicense}</span>
              <span>MERSIS {companyDetails.mersis}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
